import {
    XCircle, ShieldOff, Clock3, Users2, ReceiptText, Crown, BarChart3, Rocket,
    Phone, FileMinus, LayoutGrid, MessageSquare, Timer, UserCheck, ArrowRight, X, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/site/SectionHeading";
import { useBookDemo } from "@/context/BookDemoContext";

const WHY_CHOOSE = [
    { icon: XCircle, title: "Færre tabte opgaver", desc: "Når henvendelser, beskeder og opgaver samles ét sted, bliver færre ting glemt." },
    { icon: ShieldOff, title: "Mindre pres på kontoret", desc: "Kontoret slipper for at jagte oplysninger, status og materialer manuelt." },
    { icon: Clock3, title: "Hurtigere kundesvar", desc: "Kunder får hurtigere respons, fordi informationen bliver struktureret fra starten." },
    { icon: Users2, title: "Bedre styr på medarbejdere", desc: "Medarbejdere får opgaver direkte og kan sende status uden lange opkald." },
    { icon: ReceiptText, title: "Klarere fakturagrundlag", desc: "Tid, materialer og noter samles løbende, så faktura ikke kræver ekstra jagt." },
    { icon: Crown, title: "Mere professionel kundeoplevelse", desc: "Kunden oplever, at virksomheden har styr på processen fra første kontakt til afslutning." },
    { icon: BarChart3, title: "Bedre ledelsesoverblik", desc: "Ledelsen kan se opgaver, status, tid og belastning uden at samle information manuelt." },
    { icon: Rocket, title: "System der kan udvides", desc: "Start med ét flow og udvid med flere kundeflows, medarbejderflows og kapacitet efter behov." },
];

const BENEFITS_QUICK = [
    { icon: Phone, title: "Færre tabte henvendelser", desc: "Alle henvendelser samles ét sted og bliver håndteret konsekvent." },
    { icon: Clock3, title: "Hurtigere svar til kunder", desc: "Tydeligt flow gør, at jeres team kan svare langt hurtigere." },
    { icon: FileMinus, title: "Mindre manuelt kontorarbejde", desc: "Automatisering af gentagne opgaver frigør tid på kontoret." },
    { icon: LayoutGrid, title: "Bedre overblik over opgaver", desc: "Se status, prioritet og fordeling i ét samlet view." },
    { icon: MessageSquare, title: "Klarere kommunikation", desc: "Kontoret og medarbejderne arbejder i samme flow uden støj." },
    { icon: Timer, title: "Styr på tid og materialer", desc: "Tid og materialer registreres løbende — uden Excel-tricks." },
    { icon: ReceiptText, title: "Fakturagrundlag samlet", desc: "Alt nødvendigt data er klar, når fakturaen skal sendes." },
    { icon: UserCheck, title: "Bedre opfølgning", desc: "Genbesøg, service og opfølgning glider naturligt ind i flowet." },
];

const BEFORE = [
    "Opgaver ligger i telefon, SMS og noter",
    "Medarbejdere skal ringes op",
    "Tid og materialer mangler",
    "Faktura kræver ekstra opfølgning",
    "Kontoret mangler samlet overblik",
    "Kunder venter længere på svar",
    "Opgaver kan blive glemt eller forsinket",
    "Information ligger spredt mellem personer",
    "Dobbeltarbejde skaber spildtid",
    "Ledelsen mangler driftsoverblik",
];
const AFTER = [
    "Opgaver samles ét sted",
    "Medarbejdere får besked direkte",
    "Tid og materialer registreres løbende",
    "Kontoret får klar dokumentation",
    "Kunden får hurtigere opfølgning",
    "Fakturagrundlag er samlet automatisk",
    "Teamet arbejder ud fra samme information",
    "Ledelsen får bedre overblik over drift og opgaver",
    "Mindre dobbeltarbejde i hverdagen",
    "Flere opgaver kan afsluttes uden ekstra jagt på oplysninger",
];

export default function Fordele() {
    const { open: openDemo } = useBookDemo();

    return (
        <div data-testid="page-fordele">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute top-20 -left-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-14 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-100">
                        Fordele
                    </span>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 leading-[1.05]">
                        Fordele der{" "}
                        <span className="gradient-text">kan mærkes i hverdagen.</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        MyServiceHelper hjælper med at reducere rod, samle information og skabe bedre flow fra første henvendelse til afsluttet opgave.
                    </p>
                </div>
            </section>

            {/* Why choose */}
            <section className="pb-16 bg-white border-y border-slate-200 py-16" data-testid="why-choose-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading
                        eyebrow="Værdi"
                        title="Hvorfor virksomheder vælger"
                        gradientWord="MyServiceHelper"
                        subtitle="Konkret værdi i hverdagen — fra første henvendelse til opfølgning."
                    />
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {WHY_CHOOSE.map((w, i) => {
                            const Icon = w.icon;
                            return (
                                <div key={i} data-testid={`why-card-${i}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-blue-200 transition-all">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 inline-flex items-center justify-center ring-1 ring-blue-100">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <h3 className="mt-4 text-base font-semibold text-slate-900">{w.title}</h3>
                                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Quick benefits grid */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {BENEFITS_QUICK.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <div key={i} data-testid={`fordele-card-${i}`} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 inline-flex items-center justify-center ring-1 ring-blue-100">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <h3 className="mt-4 text-base font-semibold text-slate-900">{b.title}</h3>
                                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Before / After */}
            <section className="py-16 sm:py-20 bg-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading
                        eyebrow="Før og efter"
                        title="Fra kaos til"
                        gradientWord="overblik"
                        subtitle="Den samme dag — men med en helt anden følelse på kontoret og i marken."
                    />

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
                        <div data-testid="before-card" className="rounded-2xl bg-slate-50 border border-slate-200 p-6 sm:p-7">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-rose-700 text-xs font-semibold uppercase tracking-wider border border-rose-100">
                                <X className="w-3 h-3" />
                                Før
                            </span>
                            <ul className="mt-5 space-y-2.5">
                                {BEFORE.map((t, i) => (
                                    <li key={i} data-testid={`before-item-${i}`} className="flex items-start gap-3 text-sm text-slate-700">
                                        <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-rose-50 text-rose-500 inline-flex items-center justify-center">
                                            <X className="w-3 h-3" />
                                        </span>
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div data-testid="after-card" className="relative rounded-2xl p-[2px] bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 shadow-xl shadow-blue-500/15">
                            <div className="rounded-[14px] bg-white p-6 sm:p-7 h-full">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wider border border-emerald-100">
                                    <Check className="w-3 h-3" />
                                    Efter
                                </span>
                                <ul className="mt-5 space-y-2.5">
                                    {AFTER.map((t, i) => (
                                        <li key={i} data-testid={`after-item-${i}`} className="flex items-start gap-3 text-sm text-slate-800">
                                            <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                                                <Check className="w-3 h-3" />
                                            </span>
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <p data-testid="team-note" className="mt-10 text-center text-base sm:text-lg text-slate-700 max-w-2xl mx-auto leading-relaxed">
                        <span className="font-semibold">MyServiceHelper erstatter ikke dit team.</span>{" "}
                        Det giver dit team et bedre system at arbejde ud fra.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Vil du se det i praksis?</h3>
                    <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                        Vi gennemgår flowet fra kundehenvendelse til færdig opgave — på under 30 minutter.
                    </p>
                    <div className="mt-5">
                        <Button
                            data-testid="fordele-cta-btn"
                            onClick={() => openDemo()}
                            className="rounded-full h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            Book gratis demo <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
